import { FlexProps } from "@chakra-ui/react/flex";
import { isFuzeyMerchant } from "util/methods";
import { UserPermissionsKey } from "util/permissions";
import { AgentMerchantDomain } from "./agent-merchant-domain";
import { AgentMerchantRoleDomain } from "./agent-merchant-role-domain";
import AgentNotificationConfigDomain from "./agent-notification-config-domain";
import { AgentStylePreferences } from "./agent-style-preferences";

export default class AgentDomain {
  id: number;

  name: string;

  surname: string;

  email: string;

  phone: string;

  preferredContactChannel: string;

  notificationConfig: AgentNotificationConfigDomain | null;

  merchants: AgentMerchantDomain[];

  userId: string;

  profilePic: string;

  profilePicFile?: File | null;

  color: string;

  playerIds: string[] | null;

  stylePreferences: AgentStylePreferences | null;

  lastActiveAt: Date | null;

  constructor(
    id: number,
    name: string,
    surname: string,
    email: string,
    phone: string,
    preferredContactChannel: string,
    notificationConfig: AgentNotificationConfigDomain | null,
    merchants: AgentMerchantDomain[],
    userId: string,
    profilePic: string,
    color: string,
    playerIds: string[] | null,
    stylePreferences: AgentStylePreferences | null,
    lastActiveAt: Date | null
  ) {
    this.id = id;
    this.name = name;
    this.surname = surname;
    this.email = email;
    this.phone = phone;
    this.preferredContactChannel = preferredContactChannel;
    this.notificationConfig = notificationConfig;
    this.merchants = merchants;
    this.userId = userId;
    this.profilePic = profilePic;
    this.color = color;
    this.playerIds = playerIds;
    this.stylePreferences = stylePreferences;
    this.lastActiveAt = lastActiveAt;
  }

  public getGroupName(): string {
    const firstMerchantsGroupName = this.merchants[0].getGroupName();

    if (
      !firstMerchantsGroupName ||
      this.merchants.some((m) => m.getGroupName() !== firstMerchantsGroupName)
    ) {
      return "";
    }

    return firstMerchantsGroupName;
  }

  public getFullName(): string {
    return `${this.name} ${this.surname}`;
  }

  public getPermissions(merchantId: number): UserPermissionsKey[] {
    return this.getRole(merchantId).permissions.map((p) => {
      return p as UserPermissionsKey;
    });
  }

  public getPicture() {
    if (this.profilePic && this.profilePic !== "null") {
      return this.profilePic;
    }

    return "";
  }

  public isActive(merchantId: number) {
    return this.merchants.filter((m) => m.id === merchantId)[0].isActive;
  }

  public rotationEnabled(merchantId: number) {
    return this.merchants.filter((m) => m.id === merchantId)[0].rotationEnabled;
  }

  public getRole(merchantId: number): AgentMerchantRoleDomain {
    return this.merchants.filter((m) => m.id === merchantId)[0].role;
  }

  public hasAccessToFuzeyMerchant(): boolean {
    return this.merchants.filter((m) => isFuzeyMerchant(m.name)).length > 0;
  }

  public getChatBackgroundStyles(colorMode: "dark" | "light"): FlexProps {
    if (!this.stylePreferences?.chatBackground) {
      return {
        background: `no-repeat url("/preview-background-${colorMode}.webp")`,
        backgroundSize: "cover",
      };
    }

    const value = this.stylePreferences.chatBackground;

    if (value.startsWith("http")) {
      return {
        background: `no-repeat url("${value}")`,
        backgroundSize: "cover",
      };
    }

    // "white" / "black" render as the literal solid color in both modes —
    // the user explicitly chose pure white or pure black, not a mode-adaptive
    // shade.
    if (value === "#FFF" || value === "#000") {
      return { background: value };
    }

    return {
      background: {
        _light: `${value}.300`,
        _dark: `${value}.800`,
      },
    };
  }

  // Whether the chat background renders an image (default preview JPG or a
  // user-supplied URL) vs a solid color tint. System text rendered over an
  // image needs its own pill background for legibility; over a solid color,
  // semantic `fg.*` text reads fine without any pill chrome.
  public isChatBackgroundImage(): boolean {
    if (!this.stylePreferences?.chatBackground) {
      return true;
    }
    return this.stylePreferences.chatBackground.startsWith("http");
  }
}
